import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, AlertOctagon, Ban, Trash2, Plus, X, Loader2 } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import PdfImport from "@/components/PdfImport";
import ContractSection from "@/components/ContractSection";
import PaymentsSection from "@/components/PaymentsSection";
import InvoicesSection from "@/components/InvoicesSection";
import IndyClientExport from "@/components/IndyClientExport";
import { EVENT_TYPES, STATUSES, fmtEUR, fmtDate } from "@/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const emptyForm = {
  client: { name: "", phone: "", email: "", address: "", postal_code: "", city: "" },
  event: { date: "", city: "", postal_code: "", address: "", type: "Mariage", type_other: "", time_constraints: "" },
  logistics: { booth_id: "", delivery_at: "", pickup_at: "", stephane: { delivery: false, delivery_cost: "", pickup: false, pickup_cost: "" } },
  pricing: { formula: "200", formula_price: 280, delivery_fee: 0, options: [], discount: 0, other_fee: 0, cabine_fee: 0, consumable_free: false, acompte: "", payment_mode: "", invoice_number: "", doc_date: "", total: 0 },
  extras: { cabine_agb: false, agb_offert: false, accessoires_prevus: true },
  kind: "photobooth",
  status: "Demande",
  notes: "",
};

const VERDICTS = {
  DISPONIBLE: { label: "DISPONIBLE", icon: CheckCircle2, cls: "border-emerald-300 bg-emerald-50 text-emerald-800", dot: "text-emerald-600" },
  FAISABLE: { label: "FAISABLE", icon: CheckCircle2, cls: "border-emerald-300 bg-emerald-50 text-emerald-800", dot: "text-emerald-600" },
  A_ETUDIER: { label: "À ÉTUDIER", icon: AlertTriangle, cls: "border-amber-300 bg-amber-50 text-amber-900", dot: "text-amber-600" },
  DECONSEILLE: { label: "DÉCONSEILLÉ", icon: AlertOctagon, cls: "border-rose-300 bg-rose-50 text-rose-800", dot: "text-rose-600" },
  COMPLET: { label: "COMPLET", icon: Ban, cls: "border-rose-300 bg-rose-50 text-rose-800", dot: "text-rose-600" },
};

function addDays(dateStr, days, time) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return `${d.toISOString().slice(0, 10)}T${time}`;
}

const slotStart = (label) => {
  const m = /^(\d{1,2})h(\d{2})/.exec(label || "");
  return m ? `${m[1].padStart(2, "0")}:${m[2]}` : "";
};

const SLOT_CHOICES = [["09:00", "Matin (9h–12h)"], ["14:00", "Après-midi (14h–18h)"], ["19:00", "Soir (19h00–20h30)"]];

function StandaloneSlot({ label, value, onChange, tid }) {
  const d = value ? value.slice(0, 10) : "";
  const t = value && value.length > 11 ? value.slice(11, 16) : "";
  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-slate-600">{label}</Label>
      <div className="grid grid-cols-2 gap-2">
        <Input data-testid={`${tid}-date`} type="date" value={d} onChange={(e) => onChange(e.target.value ? `${e.target.value}T${t}` : "")} />
        <select data-testid={`${tid}-slot`} value={SLOT_CHOICES.some(([h]) => h === t) ? t : ""} onChange={(e) => { if (d && e.target.value) onChange(`${d}T${e.target.value}`); }} className="h-10 rounded-md border border-input bg-white px-2 text-sm">
          <option value="">Créneau…</option>
          {SLOT_CHOICES.map(([h, l]) => <option key={h} value={h}>{l}</option>)}
        </select>
      </div>
      {d && <p className="text-xs font-semibold text-sky-700">{fmtDate(d)}</p>}
    </div>
  );
}

function ProfitPanel({ total, form, settings }) {
  const f = settings.formulas.find((x) => x.id === form.pricing.formula) || {};
  const taxRate = parseFloat(settings.tax_rate) || 0;
  const charges = total * taxRate / 100;
  const consumable = form.pricing.consumable_free ? 0 : (parseFloat(f.consumable) || 0);
  const bank = parseFloat(f.bank_fee) || 0;
  const accessories = form.kind === "photobooth" ? (parseFloat(f.accessories) || 0) : 0;
  const st = form.logistics.stephane || {};
  const stephane = (st.delivery ? parseFloat(st.delivery_cost) || 0 : 0) + (st.pickup ? parseFloat(st.pickup_cost) || 0 : 0);
  const profit = total - charges - consumable - bank - accessories - stephane;
  const Row = ({ label, value, bold, green }) => (
    <div className={`flex justify-between text-sm ${bold ? "font-bold pt-1.5 border-t border-slate-200" : ""}`}>
      <span className="text-slate-600">{label}</span>
      <span className={`font-mono tabular-nums ${green ? "text-emerald-700 font-bold" : ""}`}>{value}</span>
    </div>
  );
  return (
    <div data-testid="booking-profit-panel" className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 space-y-1">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Rentabilité interne (estimation)</p>
      <Row label="Chiffre d'affaires" value={fmtEUR(total)} />
      <Row label={`Charges ${taxRate} % (URSSAF + versement libératoire)`} value={`− ${fmtEUR(charges)}`} />
      <Row label="Coût consommable" value={`− ${fmtEUR(consumable)}`} />
      <Row label="Frais bancaires" value={`− ${fmtEUR(bank)}`} />
      <Row label="Accessoires" value={`− ${fmtEUR(accessories)}`} />
      {stephane > 0 && <Row label="Livraison/reprise Stéphane" value={`− ${fmtEUR(stephane)}`} />}
      <Row label="Bénéfice net estimé" value={fmtEUR(profit)} bold green />
    </div>
  );
}

function ChecklistSection({ id, booth, extras, kind }) {
  const [items, setItems] = useState(null);
  const hasBooth = booth !== "" && booth !== null && booth !== undefined;
  const show = hasBooth || (kind && kind !== "photobooth");

  useEffect(() => {
    if (show) api.get(`/reservations/${id}/checklist`).then((r) => setItems(r.data.items)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, booth, extras.cabine_agb, extras.agb_offert]);

  if (!show) return null;
  if (!items) return null;

  const checked = items.filter((i) => i.done).length;
  const complete = items.length > 0 && checked === items.length;

  const toggle = async (idx) => {
    const next = items.map((it, i) => (i === idx ? { ...it, done: !it.done } : it));
    setItems(next);
    try {
      await api.put(`/reservations/${id}/checklist`, { items: next });
    } catch {
      toast.error("Erreur de sauvegarde de la check-list");
    }
  };

  return (
    <section data-testid="checklist-section" className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-heading text-lg font-semibold">Préparer le matériel</h2>
        <span data-testid="checklist-progress-label" className="text-sm font-semibold text-slate-600">Préparation : {checked} / {items.length} éléments</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div data-testid="checklist-progress-bar" className={`h-full transition-[width] ${complete ? "bg-emerald-500" : "bg-sky-500"}`} style={{ width: `${items.length ? (checked / items.length) * 100 : 0}%` }} />
      </div>
      <p data-testid="checklist-status" className={`text-sm font-semibold ${complete ? "text-emerald-600" : "text-amber-600"}`}>
        {complete ? "✓ Matériel complet — prêt pour livraison" : "⚠ Préparation incomplète"}
      </p>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {items.map((it, i) => (
          <li key={it.label}>
            <label className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${it.done ? "border-emerald-300 bg-emerald-50" : "border-slate-200 hover:border-sky-300"}`}>
              <input type="checkbox" data-testid={`checklist-item-${i}`} checked={it.done} onChange={() => toggle(i)} className="h-4 w-4 accent-sky-600" />
              <span className={`text-sm ${it.done ? "line-through text-slate-400" : ""}`}>{it.label}</span>
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Section({ title, children, testid }) {
  return (
    <section data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 space-y-4">
      <h2 className="font-heading text-lg font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function Field({ label, children, testid }) {
  return (
    <div className="space-y-1.5">
      <Label data-testid={testid ? `${testid}-label` : undefined} className="text-sm text-slate-600">{label}</Label>
      {children}
    </div>
  );
}

export default function BookingForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState(emptyForm);
  const [settings, setSettings] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [overrideAccepted, setOverrideAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customPickup, setCustomPickup] = useState(false);

  const set = (path, value) => {
    setForm((f) => {
      const next = structuredClone(f);
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  // Pré-remplissage depuis la vérification express
  useEffect(() => {
    if (!isEdit && location.state?.date) {
      set("event.date", location.state.date);
      if (location.state.city) set("event.city", location.state.city);
      if (location.state.verdict) toast.info(`Vérification : ${location.state.city} — ${location.state.verdict}`);
      if (location.state.delivery_fee) set("pricing.delivery_fee", location.state.delivery_fee);
      if (location.state.event_type) set("event.type", location.state.event_type);
    }
    if (!isEdit && location.state?.kinds?.length) {
      const ks = location.state.kinds;
      set("kind", ks.includes("photobooth") ? "photobooth" : ks[0]);
    } else if (!isEdit && location.state?.kind) set("kind", location.state.kind);
    if (!isEdit && location.state?.formula) set("pricing.formula", location.state.formula);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      if (!isEdit) {
        const ks = location.state?.kinds || [];
        const av = location.state?.availability || {};
        const k = ks.includes("photobooth") ? "photobooth" : (ks[0] || location.state?.kind);
        if (k === "agb") {
          set("pricing.formula", "autre");
          set("pricing.formula_price", r.data.agb_price || 0);
        } else if (k === "cabine") {
          set("pricing.formula", "autre");
          set("pricing.formula_price", 0);
          set("pricing.cabine_fee", r.data.cabine_price ?? 100);
          set("extras.cabine_agb", true);
        } else {
          const chosen = (location.state?.formula && r.data.formulas.find((f) => f.id === location.state.formula)) || r.data.formulas.find((f) => f.id === "200");
          if (chosen) {
            set("pricing.formula", chosen.id);
            set("pricing.formula_price", chosen.price);
          }
          if (ks.includes("cabine") && av.cabine !== "no") {
            set("extras.cabine_agb", true);
            set("pricing.cabine_fee", r.data.cabine_price ?? 100);
          }
          if (ks.includes("agb") && av.agb !== "no") set("extras.agb_offert", true);
        }
      }
    });
    if (isEdit) {
      api.get(`/reservations/${id}`).then((r) => {
        const d = r.data;
        setForm({
          ...emptyForm,
          ...d,
          pricing: { ...emptyForm.pricing, ...(d.pricing || {}), options: (d.pricing?.options) || [] },
          logistics: { ...emptyForm.logistics, ...d.logistics, booth_id: d.logistics?.booth_id ?? "" },
        });
      }).catch(() => { toast.error("Réservation introuvable"); navigate("/reservations"); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Logistique par défaut selon le type d'événement
  useEffect(() => {
    if (isEdit || !form.event.date) return;
    const t = form.event.type;
    // Dimanche : reprise le soir même (horaire à définir) ; autres jours : lendemain 11h00
    const isSunday = new Date(`${form.event.date}T00:00`).getDay() === 0;
    const pickupDefault = isSunday ? addDays(form.event.date, 0, "19:00") : addDays(form.event.date, 1, "11:00");
    if (t === "Mariage") {
      set("logistics.delivery_at", addDays(form.event.date, -1, "17:00"));
      set("logistics.pickup_at", pickupDefault);
    } else if (t === "Anniversaire") {
      set("logistics.delivery_at", addDays(form.event.date, 0, "09:00"));
      set("logistics.pickup_at", pickupDefault);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.event.date, form.event.type]);

  // Analyse de disponibilité / 3e photobooth
  useEffect(() => {
    const { delivery_at, pickup_at } = form.logistics;
    if (form.kind !== "photobooth" || !delivery_at || !pickup_at) { setAnalysis(null); return; }
    setAnalyzing(true);
    const t = setTimeout(() => {
      api.post("/logistics/analyze", {
        delivery_at, pickup_at, city: form.event.city, postal_code: form.event.postal_code || null, exclude_id: id || null,
      }).then((r) => {
        setAnalysis(r.data);
        if (!isEdit && form.logistics.booth_id === "" && r.data.free_booths?.length) {
          set("logistics.booth_id", r.data.free_booths[0]);
        }
      }).catch(() => setAnalysis(null)).finally(() => setAnalyzing(false));
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.logistics.delivery_at, form.logistics.pickup_at, form.event.city, form.event.postal_code, form.kind]);

  const total = useMemo(() => {
    const p = form.pricing;
    const t = (parseFloat(p.formula_price) || 0) + (parseFloat(p.delivery_fee) || 0)
      + (p.options || []).reduce((s, o) => s + (parseFloat(o.amount) || 0), 0)
      + (parseFloat(p.other_fee) || 0) + (parseFloat(p.cabine_fee) || 0) - (parseFloat(p.discount) || 0);
    return Math.max(t, 0);
  }, [form.pricing]);

  // Garde-fou saisie : incohérences dates logistiques vs date de prestation (avertissement non bloquant)
  const logisticsWarnings = useMemo(() => {
    const w = [];
    const ev = form.event.date;
    if (!ev) return w;
    const evD = new Date(`${ev}T00:00`);
    const DAY = 86400000;
    const dDate = (form.logistics.delivery_at || "").slice(0, 10);
    const pDate = (form.logistics.pickup_at || "").slice(0, 10);
    if (dDate.length === 10) {
      const diff = Math.round((new Date(`${dDate}T00:00`) - evD) / DAY);
      if (diff > 0) w.push(`La livraison (${fmtDate(dDate)}) est APRÈS la prestation (${fmtDate(ev)}).`);
      else if (diff < -5) w.push(`La livraison (${fmtDate(dDate)}) est ${-diff} jours AVANT la prestation (${fmtDate(ev)}) — vérifier la date.`);
    }
    if (pDate.length === 10) {
      const diff = Math.round((new Date(`${pDate}T00:00`) - evD) / DAY);
      if (diff < 0) w.push(`La reprise (${fmtDate(pDate)}) est AVANT la prestation (${fmtDate(ev)}).`);
      else if (diff > 5) w.push(`La reprise (${fmtDate(pDate)}) est ${diff} jours APRÈS la prestation (${fmtDate(ev)}) — faute de frappe possible sur le mois, vérifier la date.`);
    }
    if (dDate.length === 10 && pDate.length === 10 && pDate < dDate) w.push("La reprise est avant la livraison.");
    return w;
  }, [form.event.date, form.logistics.delivery_at, form.logistics.pickup_at]);

  // Saisie automatique adresse client : CP → ville(s), ville → CP (via /cities)
  const [clientSug, setClientSug] = useState([]);
  const [showClientSug, setShowClientSug] = useState(false);
  const onClientCity = (v) => {
    set("client.city", v);
    setShowClientSug(true);
    if (v.trim().length >= 2) api.get("/cities", { params: { q: v } }).then((r) => setClientSug(r.data)).catch(() => {});
    else setClientSug([]);
  };
  const onClientPostcode = (v) => {
    set("client.postal_code", v);
    if (/^\d{5}$/.test(v.trim())) {
      api.get("/cities", { params: { postcode: v.trim() } }).then((r) => {
        setClientSug(r.data);
        if (r.data.length === 1) set("client.city", r.data[0].name);
        else if (r.data.length > 1) setShowClientSug(true);
      }).catch(() => {});
    }
  };
  const pickClientCity = (s) => {
    set("client.city", s.name);
    if (s.postcode) set("client.postal_code", s.postcode);
    setClientSug([]);
    setShowClientSug(false);
  };

  const onFormulaChange = (fid) => {
    set("pricing.formula", fid);
    const f = settings?.formulas.find((x) => x.id === fid);
    if (f && fid !== "autre") set("pricing.formula_price", f.price);
  };

  const cancelPrestation = async () => {
    if (!window.confirm("Annuler la prestation ? L'acompte encaissé est conservé, le solde est supprimé du chiffre d'affaires prévisionnel et le matériel redevient disponible.")) return;
    try {
      await api.post(`/reservations/${id}/cancel`);
      toast.success("Prestation annulée — acompte conservé, solde annulé, matériel libéré");
      navigate("/reservations");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'annulation");
    }
  };

  const save = async () => {
    if (!form.client.name.trim()) { toast.error("Le nom du client est obligatoire"); return; }
    setSaving(true);
    const payload = {
      ...form,
      logistics: { ...form.logistics, booth_id: form.logistics.booth_id === "" ? null : Number(form.logistics.booth_id) },
      pricing: { ...form.pricing, formula_price: parseFloat(form.pricing.formula_price) || 0, delivery_fee: parseFloat(form.pricing.delivery_fee) || 0, discount: parseFloat(form.pricing.discount) || 0, other_fee: parseFloat(form.pricing.other_fee) || 0, cabine_fee: parseFloat(form.pricing.cabine_fee) || 0, total },
    };
    if ((parseFloat(payload.pricing.acompte) || 0) > 0 && !payload.pricing.acompte_paid_at) {
      payload.pricing.acompte_paid_at = new Date().toISOString().slice(0, 10);
    }
    try {
      if (isEdit) {
        await api.put(`/reservations/${id}`, payload);
        toast.success("Réservation mise à jour");
      } else if (secondKind) {
        await api.post("/reservations", payload);
        const price2 = secondKind === "cabine" ? (parseFloat(settings?.cabine_price) || 100) : (parseFloat(settings?.agb_price) || 0);
        await api.post("/reservations", {
          ...payload,
          kind: secondKind,
          pricing: { ...payload.pricing, formula_price: secondKind === "agb" ? price2 : 0, cabine_fee: secondKind === "cabine" ? price2 : 0, delivery_fee: 0, options: [], total: price2 },
          extras: { cabine_agb: secondKind === "cabine", agb_offert: false, accessoires_prevus: true },
          notes: (payload.notes ? payload.notes + " " : "") + "[2e matériel de la même demande client]",
        });
        toast.success("2 réservations enregistrées (même client, même événement)");
      } else {
        await api.post("/reservations", payload);
        toast.success("Réservation enregistrée");
      }
      navigate("/reservations");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer définitivement cette réservation ?")) return;
    await api.delete(`/reservations/${id}`);
    toast.success("Réservation supprimée");
    navigate("/reservations");
  };

  const ops = form.ops || {};
  const handed = ops.agb_handed_at || ops.cabine_handed_at;
  const returned = ops.agb_returned_at || ops.cabine_returned_at;
  const opsLabel = !isEdit ? null : form.kind === "photobooth"
    ? (ops.pickup_departure_at ? "Retourné" : ops.delivery_departure_at ? "En prestation — reprise à effectuer" : "À préparer")
    : (returned ? "Retourné" : handed ? "Chez le client — retour attendu" : "À remettre au client");
  const pickupDate = (form.logistics.pickup_at || "").slice(0, 10);
  const pickupSlotChoice = customPickup
    ? "custom"
    : ((settings?.pickup_slots || []).includes(form.logistics.pickup_slot) ? form.logistics.pickup_slot : (form.logistics.pickup_slot ? "custom" : ""));
  const pickupReady = pickupDate.length === 10 && (form.logistics.pickup_at || "").length >= 16;

  const validatePickup = async () => {
    if (!pickupReady) { toast.error("Choisissez la date et le créneau de reprise"); return; }
    try {
      const r = await api.post(`/reservations/${id}/actions`, { action: "pickup_planned", pickup_at: form.logistics.pickup_at, slot_label: form.logistics.pickup_slot || "" });
      set("ops", r.data.ops || {});
      set("history", r.data.history || []);
      toast.success((r.data.history || []).slice(-1)[0]?.label || "Reprise enregistrée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };
  const v = analysis?.verdict ? VERDICTS[analysis.verdict] : null;
  const needsOverride = analysis && ["A_ETUDIER", "DECONSEILLE", "COMPLET"].includes(analysis.verdict);
  const isStandalone = form.kind !== "photobooth";
  const secondKind = !isEdit && isStandalone
    ? ((location.state?.kinds || []).filter((k) => k !== "photobooth" && k !== form.kind)[0] || null)
    : null;
  const kindLabel = (k) => (k === "agb" ? "Audio Guest Book" : k === "cabine" ? "Cabine Audio Guest Book" : "Photobooth");

  const setKind = (k) => {
    set("kind", k);
    if (k === "agb") {
      set("extras.cabine_agb", false);
      set("extras.agb_offert", false);
      set("pricing.cabine_fee", 0);
      set("pricing.formula", "autre");
      set("pricing.formula_price", settings?.agb_price ?? 0);
      set("pricing.delivery_fee", 0);
      set("logistics.booth_id", "");
    } else if (k === "cabine") {
      set("extras.cabine_agb", true);
      set("extras.agb_offert", false);
      set("pricing.cabine_fee", settings?.cabine_price ?? 100);
      set("pricing.formula", "autre");
      set("pricing.formula_price", 0);
      set("pricing.delivery_fee", 0);
      set("logistics.booth_id", "");
    } else {
      set("pricing.cabine_fee", 0);
      set("extras.cabine_agb", false);
    }
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="booking-form">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
            {isEdit ? "Fiche réservation" : "Nouvelle réservation"}
          </h1>
          {isEdit && opsLabel && <p data-testid="ops-status-badge" className="text-xs font-semibold text-sky-700 mt-1">Statut matériel : {opsLabel}</p>}
        </div>
        {isEdit && (
          <Button variant="outline" data-testid="booking-delete-button" onClick={remove} className="text-rose-600 border-rose-200 hover:bg-rose-50">
            <Trash2 className="h-4 w-4 mr-1" /> Supprimer
          </Button>
        )}
      </div>

      {!isEdit && <PdfImport />}

      <div className="flex rounded-xl border border-slate-200 bg-white p-1 gap-1" data-testid="booking-kind-toggle">
        {[["photobooth", "Photobooth"], ["agb", "Audio Guest Book seul"], ["cabine", "Cabine AGB seule"]].map(([k, label]) => (
          <button key={k} type="button" data-testid={`booking-kind-${k}`} onClick={() => setKind(k)}
            className={`flex-1 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${form.kind === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
      </div>

      {secondKind && (
        <p data-testid="booking-second-kind-note" className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
          Deux matériels sélectionnés : l'enregistrement créera 2 réservations — {kindLabel(form.kind)} + {kindLabel(secondKind)} (même client, même événement, retrait à Lesménils).
        </p>
      )}

      <Section title="Client" testid="section-client">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Nom / prénom *">
            <Input data-testid="booking-form-client-name" value={form.client.name} onChange={(e) => set("client.name", e.target.value)} placeholder="Ex. Marie Dupont" />
          </Field>
          <Field label="Téléphone">
            <Input data-testid="booking-form-client-phone" value={form.client.phone} onChange={(e) => set("client.phone", e.target.value)} placeholder="06 …" />
          </Field>
          <Field label="Email">
            <Input data-testid="booking-form-client-email" type="email" value={form.client.email} onChange={(e) => set("client.email", e.target.value)} placeholder="client@email.fr" />
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Adresse">
            <Input data-testid="booking-form-client-address" value={form.client.address || ""} onChange={(e) => set("client.address", e.target.value)} placeholder="N° et rue" />
          </Field>
          <Field label="Code postal">
            <Input data-testid="booking-form-client-postcode" value={form.client.postal_code || ""} onChange={(e) => onClientPostcode(e.target.value)} placeholder="54700 — la ville se remplit seule" />
          </Field>
          <div className="relative">
            <Field label="Ville">
              <Input data-testid="booking-form-client-city" value={form.client.city || ""} onChange={(e) => onClientCity(e.target.value)} onFocus={() => setShowClientSug(true)} onBlur={() => setTimeout(() => setShowClientSug(false), 150)} placeholder="Lesménils" autoComplete="off" />
            </Field>
            {showClientSug && clientSug.length > 0 && (
              <ul className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-40 overflow-auto" data-testid="booking-form-client-city-sug">
                {clientSug.map((s) => (
                  <li key={s.label}><button type="button" data-testid={`client-city-sug-${s.postcode}-${s.name}`} className="w-full text-left px-3 py-2 text-sm hover:bg-sky-50" onMouseDown={() => pickClientCity(s)}>{s.label}</button></li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <IndyClientExport client={form.client} />
      </Section>

      <Section title="Événement" testid="section-event">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Date de l'événement">
            <Input data-testid="booking-form-event-date" type="date" value={form.event.date} onChange={(e) => set("event.date", e.target.value)} />
              {form.event.date && <p data-testid="booking-form-event-day" className="text-xs font-semibold text-sky-700">{fmtDate(form.event.date)}</p>}
          </Field>
          <Field label="Type d'événement">
            <select data-testid="booking-form-event-type" value={form.event.type} onChange={(e) => set("event.type", e.target.value)} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
              {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Ville">
            <Input data-testid="booking-form-city" value={form.event.city} onChange={(e) => set("event.city", e.target.value)} placeholder="Ex. Pont-à-Mousson" />
          </Field>
          <Field label="Code postal">
            <Input data-testid="booking-form-postal-code" value={form.event.postal_code} onChange={(e) => set("event.postal_code", e.target.value)} placeholder="54700" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Adresse exacte (facultatif)">
              <Input data-testid="booking-form-address" value={form.event.address} onChange={(e) => set("event.address", e.target.value)} placeholder="Salle des fêtes, 12 rue…" />
            </Field>
          </div>
          {form.event.type === "Autre" && (
            <div className="sm:col-span-2 space-y-4">
              <Field label="Précisez le type d'événement">
                <Input data-testid="booking-form-type-other" value={form.event.type_other} onChange={(e) => set("event.type_other", e.target.value)} placeholder="Ex. Soirée d'entreprise, kermesse…" />
              </Field>
              <Field label="Contraintes horaires">
                <Input data-testid="booking-form-time-constraints" value={form.event.time_constraints} onChange={(e) => set("event.time_constraints", e.target.value)} placeholder="Ex. salle accessible à partir de 14h…" />
              </Field>
            </div>
          )}
        </div>
      </Section>

      <Section title="Logistique" testid="section-logistics">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {isStandalone ? (
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-sm text-slate-600" data-testid="cabine-pickup-note">
              Photobooth : <strong>Aucun</strong> — matériel à retirer à Lesménils
            </div>
          ) : (
          <Field label="Photobooth attribué">
            <select data-testid="booking-form-photobooth-select" value={form.logistics.booth_id} onChange={(e) => set("logistics.booth_id", e.target.value === "" ? "" : Number(e.target.value))} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
              <option value="">— À définir —</option>
              {(settings?.booth_names || []).map((n, i) => {
                const busy = analysis && !analysis.free_booths?.includes(i);
                return <option key={i} value={i}>{n}{busy ? " (occupé)" : ""}</option>;
              })}
            </select>
          </Field>
          )}
          {isStandalone ? (
            <>
              <StandaloneSlot label="Date de retrait à Lesménils" value={form.logistics.delivery_at} onChange={(v) => set("logistics.delivery_at", v)} tid="booking-form-withdrawal" />
              <StandaloneSlot label="Date de retour à Lesménils" value={form.logistics.pickup_at} onChange={(v) => set("logistics.pickup_at", v)} tid="booking-form-return" />
            </>
          ) : null}
        </div>

        {logisticsWarnings.length > 0 && (
          <div className="rounded-xl border-2 border-amber-400 bg-amber-50 p-3 space-y-1" data-testid="logistics-date-warning">
            <p className="text-sm font-bold text-amber-800">Vérifier les dates logistiques</p>
            <ul className="list-disc pl-5 space-y-0.5">
              {logisticsWarnings.map((w, i) => <li key={i} className="text-sm text-amber-900" data-testid={`logistics-date-warning-${i}`}>{w}</li>)}
            </ul>
            <p className="text-xs text-amber-700">Ce contrôle n'empêche pas l'enregistrement — il signale une incohérence probable (faute de frappe possible).</p>
          </div>
        )}

        {isStandalone && (
          <p className="text-sm text-slate-500 rounded-lg bg-sky-50 border border-sky-200 px-3 py-2" data-testid="cabine-mode-note">
            {form.kind === "agb" ? "Audio Guest Book loué seul" : "Cabine Audio Guest Book seule"} : retrait et retour à Lesménils, aucune livraison, aucun supplément kilométrique. Choisissez les dates et créneaux de retrait / retour ci-dessus.
          </p>
        )}

        {!isStandalone && (
          <div className="rounded-xl border border-slate-200 p-3 space-y-3" data-testid="stephane-section">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Délégation à Stéphane (coût interne, jamais visible côté client)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" data-testid="stephane-delivery" className="h-4 w-4 accent-sky-600"
                  checked={form.logistics.stephane.delivery}
                  onChange={(e) => { set("logistics.stephane.delivery", e.target.checked); if (e.target.checked && !form.logistics.stephane.delivery_cost) set("logistics.stephane.delivery_cost", settings?.stephane_cost ?? 50); }} />
                Livraison par Stéphane
              </label>
              {form.logistics.stephane.delivery && (
                <Input data-testid="stephane-delivery-cost" type="number" min="0" value={form.logistics.stephane.delivery_cost} onChange={(e) => set("logistics.stephane.delivery_cost", e.target.value)} placeholder="Coût livraison (€)" />
              )}
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" data-testid="stephane-pickup" className="h-4 w-4 accent-sky-600"
                  checked={form.logistics.stephane.pickup}
                  onChange={(e) => { set("logistics.stephane.pickup", e.target.checked); if (e.target.checked && !form.logistics.stephane.pickup_cost) set("logistics.stephane.pickup_cost", settings?.stephane_cost ?? 50); }} />
                Reprise par Stéphane
              </label>
              {form.logistics.stephane.pickup && (
                <Input data-testid="stephane-pickup-cost" type="number" min="0" value={form.logistics.stephane.pickup_cost} onChange={(e) => set("logistics.stephane.pickup_cost", e.target.value)} placeholder="Coût reprise (€)" />
              )}
            </div>
          </div>
        )}

        {!isStandalone && analyzing && (
          <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="availability-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Vérification des disponibilités…
          </div>
        )}

        {!isStandalone && !analyzing && analysis?.ready && v && (
          <div data-testid="logistics-verdict-panel" className={`rounded-xl border p-4 space-y-2 ${v.cls}`}>
            <div className="flex items-center gap-2">
              <v.icon className={`h-5 w-5 ${v.dot}`} />
              <span data-testid="logistics-verdict-badge" className="font-heading font-extrabold tracking-tight">{v.label}</span>
            </div>
            <p className="text-sm">{analysis.message}</p>
            {analysis.events?.length > 0 && analysis.busy_count >= 1 && (
              <p className="text-xs opacity-80">
                Événements sur la période : {analysis.events.map((e) => `${e.client} (${e.city})`).join(" · ")}
              </p>
            )}
            {analysis.zone_warning && <p className="text-xs font-medium">{analysis.zone_warning}</p>}
            {needsOverride && !overrideAccepted && (
              <Button
                type="button"
                data-testid="logistics-override-button"
                onClick={() => { setOverrideAccepted(true); toast.info("Réservation acceptée malgré l'alerte logistique"); }}
                className="mt-1 bg-slate-900 hover:bg-slate-800 text-white transition-colors"
              >
                Accepter quand même
              </Button>
            )}
            {needsOverride && overrideAccepted && (
              <p className="text-xs font-semibold" data-testid="logistics-override-accepted">✓ Acceptée malgré l'alerte — vous pouvez enregistrer.</p>
            )}
          </div>
        )}
      </Section>

      <Section title="Financier" testid="section-pricing">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {!isStandalone && (
          <Field label="Formule choisie">
            <select data-testid="booking-form-formula-select" value={form.pricing.formula} onChange={(e) => onFormulaChange(e.target.value)} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
              {(settings?.formulas || []).map((f) => <option key={f.id} value={f.id}>{f.label}{f.id !== "autre" ? ` — ${fmtEUR(f.price)}` : ""}</option>)}
            </select>
          </Field>
          )}
          {form.pricing.formula === "700" && analysis && analysis.f700_available === false && (
            <p data-testid="booking-formula-700-warning" className="sm:col-span-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              ⚠ La formule 700 photos est déjà réservée sur cette période (une seule machine compatible).
            </p>
          )}
          <Field label={isStandalone ? "Prix de la prestation (€)" : "Prix de la formule (€)"}>
            <Input data-testid="booking-form-formula-price" type="number" min="0" step="1" value={form.pricing.formula_price} onChange={(e) => set("pricing.formula_price", e.target.value)} />
          </Field>
          {!isStandalone && (
            <Field label="Supplément livraison (€)">
              <Input data-testid="booking-form-delivery-fee" type="number" min="0" step="1" value={form.pricing.delivery_fee} onChange={(e) => set("pricing.delivery_fee", e.target.value)} />
            </Field>
          )}
          <Field label="Remise (€)">
            <Input data-testid="booking-form-discount" type="number" min="0" step="1" value={form.pricing.discount} onChange={(e) => set("pricing.discount", e.target.value)} />
          </Field>
          <Field label="Autre supplément (€)">
            <Input data-testid="booking-form-other-fee" type="number" min="0" step="1" value={form.pricing.other_fee} onChange={(e) => set("pricing.other_fee", e.target.value)} />
          </Field>
          <Field label="Acompte payé (€)">
            <Input data-testid="booking-form-acompte" type="number" min="0" step="1" value={form.pricing.acompte} onChange={(e) => set("pricing.acompte", e.target.value)} />
          </Field>
          <Field label="Date d'encaissement de l'acompte">
            <Input data-testid="booking-form-acompte-paid-at" type="date" value={form.pricing.acompte_paid_at || ""} onChange={(e) => set("pricing.acompte_paid_at", e.target.value)} />
          </Field>
          <Field label="Date d'encaissement du solde (si déjà réglé)">
            <Input data-testid="booking-form-solde-paid-at" type="date" value={form.pricing.solde_paid_at || ""} onChange={(e) => set("pricing.solde_paid_at", e.target.value)} />
          </Field>
          <label className="flex items-start gap-2 text-sm sm:col-span-2 lg:col-span-3 rounded-lg border border-violet-200 bg-violet-50/60 px-3 py-2.5">
            <input type="checkbox" data-testid="booking-urssaf-free" checked={!!form.pricing.urssaf_free} onChange={(e) => set("pricing.urssaf_free", e.target.checked)} className="h-4 w-4 mt-0.5 accent-violet-600" />
            <span>Ne pas appliquer la déduction URSSAF de 22,9 % — la prestation est <strong>exclue du CA déclaré</strong> mais reste comptée dans le bénéfice interne (coûts directs déduits).</span>
          </label>
          <Field label="Solde restant">
            <div data-testid="booking-solde" className="h-10 flex items-center px-3 rounded-md bg-slate-100 font-mono tabular-nums text-sm font-semibold">
              {fmtEUR(total - (parseFloat(form.pricing.acompte) || 0))}
            </div>
          </Field>
          <Field label="Mode de paiement de l'acompte">
            <select data-testid="booking-form-payment-mode" value={form.pricing.payment_mode} onChange={(e) => set("pricing.payment_mode", e.target.value)} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
              <option value="">—</option>
              {["Carte", "PayPal", "Virement", "Espèces", "Autre"].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="N° de facture">
            <Input data-testid="booking-form-invoice-number" value={form.pricing.invoice_number} onChange={(e) => set("pricing.invoice_number", e.target.value)} />
          </Field>
          <Field label="Date du document / facture">
            <Input data-testid="booking-form-doc-date" type="date" value={form.pricing.doc_date} onChange={(e) => set("pricing.doc_date", e.target.value)} />
          </Field>
        </div>

        {form.kind === "photobooth" && (
          <div className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid="booking-toile-block">
            {(() => {
              const toile = (form.pricing.options || []).find((o) => o.label === "Toile de fond");
              const toggle = (on) => {
                const opts = (form.pricing.options || []).filter((o) => o.label !== "Toile de fond");
                if (on) opts.unshift({ label: "Toile de fond", amount: 65, model: "" });
                set("pricing.options", opts);
              };
              const setModel = (m) => set("pricing.options", (form.pricing.options || []).map((o) => o.label === "Toile de fond" ? { ...o, model: m } : o));
              return (
                <>
                  <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                    <input type="checkbox" data-testid="booking-toile-check" className="h-4 w-4 accent-sky-600" checked={!!toile} onChange={(e) => toggle(e.target.checked)} />
                    Toile de fond — 65 €
                  </label>
                  {toile && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-slate-500">Modèle :</span>
                      {!toile.model && <span className="text-xs font-bold text-amber-600" data-testid="booking-toile-warning">⚠️ Modèle de toile de fond à sélectionner</span>}
                      {[["effet_bois", "Effet bois"], ["paillette", "Paillette"]].map(([v, l]) => (
                        <button key={v} type="button" data-testid={`booking-toile-model-${v}`} onClick={() => setModel(v)}
                          className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${toile.model === v ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 hover:border-sky-400"}`}>
                          {l}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-sm text-slate-600">Options</Label>
          {form.pricing.options.map((o, i) => (
            <div key={i} className="flex gap-2">
              <Input data-testid={`booking-form-option-label-${i}`} placeholder="Ex. Livre d'or" value={o.label} onChange={(e) => { const opts = [...form.pricing.options]; opts[i] = { ...opts[i], label: e.target.value }; set("pricing.options", opts); }} />
              <Input data-testid={`booking-form-option-amount-${i}`} type="number" min="0" step="1" className="w-28" placeholder="€" value={o.amount} onChange={(e) => { const opts = [...form.pricing.options]; opts[i] = { ...opts[i], amount: e.target.value }; set("pricing.options", opts); }} />
              <Button type="button" variant="ghost" size="icon" data-testid={`booking-form-option-remove-${i}`} onClick={() => set("pricing.options", form.pricing.options.filter((_, j) => j !== i))}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" data-testid="booking-form-add-option" onClick={() => set("pricing.options", [...form.pricing.options, { label: "", amount: 0 }])}>
            <Plus className="h-4 w-4 mr-1" /> Ajouter une option
          </Button>
        </div>

        <div className="rounded-xl bg-slate-900 text-white p-4 space-y-1" data-testid="booking-total-panel">
          <div className="flex justify-between text-sm text-slate-300"><span>Formule</span><span className="font-mono tabular-nums">{fmtEUR(parseFloat(form.pricing.formula_price) || 0)}</span></div>
          <div className="flex justify-between text-sm text-slate-300"><span>Supplément livraison</span><span className="font-mono tabular-nums">{fmtEUR(parseFloat(form.pricing.delivery_fee) || 0)}</span></div>
          {form.pricing.options.map((o, i) => (
            <div key={i} className="flex justify-between text-sm text-slate-300"><span>{o.label === "Toile de fond" ? `Toile de fond : ${o.model === "effet_bois" ? "Effet bois" : o.model === "paillette" ? "Paillette" : "modèle à sélectionner"}` : `Option : ${o.label || "—"}`}</span><span className="font-mono tabular-nums">{fmtEUR(parseFloat(o.amount) || 0)}</span></div>
          ))}
          <div className="flex justify-between text-sm text-slate-300"><span>Autre supplément</span><span className="font-mono tabular-nums">{fmtEUR(parseFloat(form.pricing.other_fee) || 0)}</span></div>
          {form.extras.agb_offert && (
            <div className="flex justify-between text-sm" data-testid="booking-agb-offert-line"><span className="text-emerald-300">Audio Guest Book</span><span className="font-mono tabular-nums text-emerald-300">Offert — 0 €</span></div>
          )}
          {(parseFloat(form.pricing.cabine_fee) || 0) > 0 && (
            <div className="flex justify-between text-sm text-slate-300"><span>Cabine Audio Guest Book</span><span className="font-mono tabular-nums">{fmtEUR(parseFloat(form.pricing.cabine_fee) || 0)}</span></div>
          )}
          <div className="flex justify-between text-sm text-slate-300"><span>Remise</span><span className="font-mono tabular-nums">− {fmtEUR(parseFloat(form.pricing.discount) || 0)}</span></div>
          <div className="flex justify-between font-heading font-extrabold text-lg pt-2 border-t border-white/20"><span>Prix total</span><span data-testid="booking-total" className="font-mono tabular-nums">{fmtEUR(total)}</span></div>
        </div>

        {settings && <ProfitPanel total={total} form={form} settings={settings} />}
      </Section>

      {!isStandalone && (
        <Section title="Reprise du matériel" testid="section-pickup-planning">
          {pickupReady && (
            <p data-testid="pickup-planned-label" className="text-sm font-semibold text-sky-700 rounded-lg bg-sky-50 border border-sky-200 px-3 py-2">
              Reprise prévue : {fmtDate(pickupDate)} — {form.logistics.pickup_slot || (form.logistics.pickup_at || "").slice(11, 16).replace(":", "h")}
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Date de reprise">
              <Input data-testid="pickup-plan-date" type="date" value={pickupDate} onChange={(e) => {
                const d = e.target.value;
                const start = slotStart(form.logistics.pickup_slot) || (form.logistics.pickup_at || "").slice(11, 16) || "";
                set("logistics.pickup_at", d ? `${d}T${start}` : "");
              }} />
            </Field>
            <Field label="Créneau de reprise">
              <select data-testid="pickup-plan-slot" value={pickupSlotChoice} onChange={(e) => {
                const val = e.target.value;
                if (val === "custom") { setCustomPickup(true); return; }
                setCustomPickup(false);
                set("logistics.pickup_slot", val);
                const start = slotStart(val);
                if (pickupDate && start) set("logistics.pickup_at", `${pickupDate}T${start}`);
                if (!val) set("logistics.pickup_slot", "");
              }} className="h-10 w-full rounded-md border border-input bg-white px-2 text-sm">
                <option value="">Choisir…</option>
                {(settings?.pickup_slots || []).map((s) => <option key={s} value={s}>{s}</option>)}
                <option value="custom">Horaire personnalisé</option>
              </select>
            </Field>
          </div>
          {pickupSlotChoice === "custom" && (
            <Field label="Heure personnalisée">
              <Input data-testid="pickup-plan-custom-time" type="time" value={(form.logistics.pickup_at || "").slice(11, 16)} onChange={(e) => {
                const tm = e.target.value;
                if (pickupDate && tm) {
                  set("logistics.pickup_at", `${pickupDate}T${tm}`);
                  set("logistics.pickup_slot", tm.replace(":", "h"));
                }
              }} />
            </Field>
          )}
          {isEdit ? (
            <Button data-testid="pickup-validate-button" onClick={validatePickup} disabled={!pickupReady} className="bg-slate-900 hover:bg-slate-800 text-white font-semibold transition-colors">
              ✓ Valider la reprise
            </Button>
          ) : (
            <p className="text-xs text-slate-400">Le jour et le créneau de reprise seront enregistrés avec la réservation.</p>
          )}
        </Section>
      )}

      {!isStandalone && (
      <Section title="Matériel complémentaire" testid="section-extras">
        <div className="space-y-3">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              data-testid="booking-extra-cabine"
              className="mt-0.5 h-4 w-4 accent-sky-600"
              checked={form.extras.cabine_agb}
              disabled={!form.extras.cabine_agb && analysis && analysis.cabine_available === false}
              onChange={(e) => { set("extras.cabine_agb", e.target.checked); set("pricing.cabine_fee", e.target.checked ? (settings?.cabine_price ?? 100) : 0); }}
            />
            <span className="text-sm">
              <span className="font-medium">Cabine Audio Guest Book — {fmtEUR(settings?.cabine_price ?? 100)}</span>
              <span className="block text-xs text-slate-500">À retirer à Lesménils, non livrée · aucun supplément kilométrique</span>
            </span>
          </label>
          {analysis && analysis.cabine_available === false && !form.extras.cabine_agb && (
            <p className="text-xs font-medium text-amber-700" data-testid="cabine-unavailable-note">Cabine Audio Guest Book : indisponible sur cette période</p>
          )}
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              data-testid="booking-extra-agb"
              className="mt-0.5 h-4 w-4 accent-sky-600"
              checked={form.extras.agb_offert}
              disabled={!form.extras.agb_offert && analysis && analysis.agb_remaining === 0}
              onChange={(e) => set("extras.agb_offert", e.target.checked)}
            />
            <span className="text-sm">
              <span className="font-medium">Audio Guest Book offert</span>
              <span className="block text-xs text-slate-500">Offert avec la location, sous réserve de disponibilité (stock : 3)</span>
            </span>
          </label>
          {analysis && analysis.agb_remaining === 0 && !form.extras.agb_offert && (
            <p className="text-xs font-medium text-amber-700" data-testid="agb-unavailable-note">Audio Guest Book offert : indisponible sur cette période</p>
          )}
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              data-testid="booking-accessoires-prevus"
              className="mt-0.5 h-4 w-4 accent-sky-600"
              checked={form.extras.accessoires_prevus !== false}
              onChange={(e) => set("extras.accessoires_prevus", e.target.checked)}
            />
            <span className="text-sm">
              <span className="font-medium">Accessoires Photobooth prévus</span>
              <span className="block text-xs text-slate-500">Décoché = aucune sortie de stock accessoires au départ en livraison (client qui n'en veut pas)</span>
            </span>
          </label>
          {settings && (settings.formulas.find((x) => x.id === form.pricing.formula)?.consumable || 0) > 0 && (
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                data-testid="booking-consumable-free"
                className="mt-0.5 h-4 w-4 accent-sky-600"
                checked={form.pricing.consumable_free}
                onChange={(e) => set("pricing.consumable_free", e.target.checked)}
              />
              <span className="text-sm">
                <span className="font-medium">Consommable déjà disponible / coût consommable = 0 €</span>
                <span className="block text-xs text-slate-500">Calcul interne de rentabilité uniquement — ne change jamais le prix client</span>
              </span>
            </label>
          )}
        </div>
      </Section>
      )}

      {isEdit && (form.documents || []).length > 0 && (
        <Section title="Documents" testid="section-documents">
          <ul className="space-y-2">
            {form.documents.map((doc, i) => (
              <li key={i}>
                <a data-testid={`document-pdf-${i}`} href={`${API_BASE}/reservations/${id}/documents/${i}/pdf`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-sky-700 hover:border-sky-400 transition-colors">
                  PDF acompte — {doc.filename}{doc.invoice_number ? ` · facture ${doc.invoice_number}` : ""}{doc.doc_date ? ` · ${doc.doc_date}` : ""}
                </a>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {isEdit && (form.history || []).length > 0 && (
        <Section title="Historique interne" testid="section-history">
          <ul className="space-y-1">
            {form.history.map((h, i) => <li key={i} data-testid={`history-item-${i}`} className="text-sm text-slate-600">✓ {h.label}</li>)}
          </ul>
        </Section>
      )}

      {isEdit && <ChecklistSection id={id} booth={form.logistics.booth_id} extras={form.extras} kind={form.kind} />}

      {isEdit && <PaymentsSection rid={id} />}
      {isEdit && <InvoicesSection rid={id} />}
      {isEdit && <ContractSection rid={id} />}

      <div className="flex flex-wrap gap-3 pb-4">
        <Button data-testid="booking-form-submit" onClick={save} disabled={saving} size="lg" className="bg-sky-500 hover:bg-sky-600 text-white font-semibold flex-1 sm:flex-none transition-colors">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {isEdit ? "Enregistrer les modifications" : "Enregistrer la réservation"}
        </Button>
        {isEdit && form.status !== "Annulée" && (
          <Button variant="outline" size="lg" data-testid="booking-cancel-prestation" onClick={cancelPrestation} className="border-rose-300 text-rose-700 hover:bg-rose-50 transition-colors">
            Annuler la prestation
          </Button>
        )}
        <Button variant="outline" size="lg" data-testid="booking-form-cancel" onClick={() => navigate(-1)}>Retour</Button>
      </div>
    </div>
  );
}
